import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { errMsg } from "@/lib/api";

const inp = "w-full h-10 px-3 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-[#B91C1C]";
const GATEWAYS = [
  { id: "chip", name: "CHIP", fields: [["chip_brand_id", "Brand ID"], ["chip_secret_key", "Secret Key", true]], help: "portal.chip-in.asia → Developers" },
  { id: "billplz", name: "Billplz", fields: [["billplz_secret_key", "API Secret Key", true], ["billplz_collection_id", "Collection ID"], ["billplz_xsign_key", "X Signature Key (pilihan)", true]], help: "Billplz → Settings → Keys & Integration" },
  { id: "bayarcash", name: "Bayarcash", fields: [["bayarcash_pat", "Personal Access Token", true], ["bayarcash_secret_key", "API Secret Key", true], ["bayarcash_portal_key", "Portal Key"]], help: "console.bayar.cash → Profile → Integration & Portals" },
  { id: "syokbill", name: "SyokBill", fields: [["syokbill_api_key", "API Key", true]], help: "syokbill.com → Settings → API Keys (minta SyokBill aktifkan API Keys untuk akaun anda)" },
];

function GatewayField({ k, label, secret, cfg, val, setVal }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium">{label}</span>
      <input type={secret ? "password" : "text"} autoComplete="off" value={val[k] ?? (secret ? "" : cfg[k] || "")}
        placeholder={secret && cfg[`${k}_hint`] ? `Disimpan: ${cfg[`${k}_hint`]} (biar kosong jika tidak tukar)` : ""}
        onChange={(e) => setVal((p) => ({ ...p, [k]: e.target.value }))} data-testid={`pay-${k}`} className={inp} />
    </label>
  );
}

export function PaymentGatewayCard({ req }) {
  const [cfg, setCfg] = useState(null);
  const [val, setVal] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { req("get", "/superadmin/payment").then(setCfg).catch((e) => toast.error(errMsg(e))); }, [req]);
  if (!cfg) return null;
  const cur = { ...cfg, ...val };
  const gw = GATEWAYS.find((g) => g.id === cur.gateway);

  const save = async () => {
    setBusy(true);
    try {
      const body = { enabled: cur.enabled, gateway: cur.gateway, sandbox: cur.sandbox };
      GATEWAYS.forEach((g) => g.fields?.forEach(([k]) => { body[k] = val[k] ?? (k.endsWith("_key") || k.endsWith("_pat") || k === "syokbill_api_key" ? "" : cfg[k] || ""); }));
      setCfg(await req("put", "/superadmin/payment", body));
      setVal({});
      toast.success("Tetapan bayaran disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-2xl border p-6 space-y-4" data-testid="payment-gateway-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-bold flex items-center gap-2"><CreditCard className="w-5 h-5 text-red-700" />Bayaran Online <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400 text-[#3B0A0A] font-bold">SUPERADMIN</span></h3>
        <span data-testid="payment-ready-badge" className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${cfg.ready ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{cfg.ready ? "Aktif" : "Tidak aktif"}</span>
      </div>
      <label className="flex items-center justify-between gap-4">
        <span className="text-sm">Benarkan pelanggan bayar online<span className="block text-xs text-slate-500">Pelanggan boleh pilih Bayar Online (FPX/kad) atau Tunai. Pesanan online dihantar ke WhatsApp & Loyverse selepas bayaran berjaya.</span></span>
        <Switch checked={!!cur.enabled} onCheckedChange={(v) => setVal((p) => ({ ...p, enabled: v }))} data-testid="pay-enabled" />
      </label>
      <div className="flex flex-wrap gap-2">
        {GATEWAYS.map((g) => (
          <button key={g.id} type="button" disabled={g.soon} data-testid={`pay-gateway-${g.id}`} onClick={() => setVal((p) => ({ ...p, gateway: g.id }))}
            className={`px-4 h-10 rounded-full text-sm font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${cur.gateway === g.id ? "bg-[#B91C1C] text-white border-[#B91C1C]" : "bg-white border-slate-200"}`}>
            {g.name}{g.soon && <span className="ml-1 text-[10px] font-normal">(akan datang)</span>}
          </button>
        ))}
      </div>
      {gw && (
        <div className="rounded-xl border bg-slate-50/60 p-4 space-y-3" data-testid={`pay-fields-${gw.id}`}>
          <p className="text-xs text-slate-500">Dapatkan kunci dari: {gw.help}</p>
          {gw.fields.map(([k, label, secret]) => <GatewayField key={k} k={k} label={label} secret={secret} cfg={cfg} val={val} setVal={setVal} />)}
          <label className="flex items-center justify-between"><span className="text-sm">Mod ujian (Sandbox)</span>
            <Switch checked={!!cur.sandbox} onCheckedChange={(v) => setVal((p) => ({ ...p, sandbox: v }))} data-testid="pay-sandbox" /></label>
          {gw.id === "chip" && <p className="text-xs text-slate-500">CHIP guna kunci ujian/live yang berbeza — mod ujian ikut jenis Secret Key.</p>}
        </div>
      )}
      <button onClick={save} disabled={busy} data-testid="pay-save" className="px-5 h-10 rounded-lg bg-[#B91C1C] text-white text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60">Simpan Tetapan Bayaran</button>
    </section>
  );
}
